// frontend/src/pages/admin/CVGeneration/EducationSection.js
import React, { useState, useEffect, useCallback } from 'react';
import { 
  GraduationCap, Plus, Edit2, Trash2, X, AlertTriangle, Clock, Save
} from 'lucide-react';
import { staffAPI, configAPI } from '../../../services/api';
import toast from '../../../utils/toast';
import Portal from '../../../components/UI/Portal';
import SearchableCombobox from '../../../components/UI/SearchableCombobox';
import { formatDate, getDataFreshness } from '../../../utils/dateUtils';

/**
 * Education Section for CV Data Entry
 * 
 * Features:
 * - Display education records in a table
 * - Add/Edit/Delete with modals
 * - Searchable Institution dropdown from Hipo library
 * - "Other" option for institutions not in library
 * - Duplicate detection (blocks save if duplicate found)
 * - Data freshness indicators
 */
const EducationSection = ({ staffId, records = [], onDataChange }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  
  // Institution search state
  const [institutions, setInstitutions] = useState([]);
  const [isLoadingInstitutions, setIsLoadingInstitutions] = useState(false);
  const [institutionDisplayName, setInstitutionDisplayName] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    education_institution: null, // FK ID from library
    education_institution_other: '', // Text for "Other" or display
    isOtherInstitution: false, // Toggle for "Other" mode
    education_qualification_name: '',
    education_field_of_study: '',
    education_start_year: '',
    education_end_year: '',
    education_is_ongoing: false,
  });

  // Fetch institutions from Hipo library (called by SearchableCombobox onSearch)
  const fetchInstitutions = useCallback(async (search = '') => {
    setIsLoadingInstitutions(true);
    try {
      const response = await configAPI.getEducationInstitutions({ 
        search: search,
        page_size: 50,
        ordering: 'name'
      });
      const results = response.data?.results || response.data || [];
      setInstitutions(results);
    } catch (error) {
      console.error('Error fetching institutions:', error);
      setInstitutions([]);
    } finally {
      setIsLoadingInstitutions(false);
    }
  }, []);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (showModal && editingRecord) {
      const usesFK = editingRecord.education_institution !== null;
      setFormData({
        education_institution: editingRecord.education_institution || null,
        education_institution_other: editingRecord.education_institution_other || editingRecord.institution_display_name || '',
        isOtherInstitution: !usesFK && !!editingRecord.education_institution_other,
        education_qualification_name: editingRecord.education_qualification_name || '',
        education_field_of_study: editingRecord.education_field_of_study || '',
        education_start_year: editingRecord.education_start_year || '',
        education_end_year: editingRecord.education_end_year || '',
        education_is_ongoing: editingRecord.education_is_ongoing || false,
      });
      setInstitutionDisplayName(editingRecord.institution_display_name || '');
    } else if (showModal) {
      setFormData({
        education_institution: null,
        education_institution_other: '',
        isOtherInstitution: false,
        education_qualification_name: '',
        education_field_of_study: '',
        education_start_year: new Date().getFullYear() - 4,
        education_end_year: new Date().getFullYear(),
        education_is_ongoing: false,
      });
      setInstitutionDisplayName('');
    }
    setDuplicateWarning(null);
  }, [showModal, editingRecord]);

  const getInstitutionDisplayName = () => {
    if (formData.isOtherInstitution) {
      return formData.education_institution_other;
    }
    if (formData.education_institution) {
      const inst = institutions.find(i => i.id === formData.education_institution);
      return inst?.name || institutionDisplayName;
    }
    return institutionDisplayName;
  };

  // Check for duplicates
  const checkDuplicate = () => {
    const displayName = getInstitutionDisplayName();
    const isDuplicate = records.some(record => {
      // Skip the record being edited
      if (editingRecord && record.id === editingRecord.id) return false;
      
      const sameInstitution = (
        record.institution_display_name?.toLowerCase() === displayName?.toLowerCase()
      );
      const sameQualification = record.education_qualification_name?.toLowerCase() === formData.education_qualification_name?.toLowerCase();
      const sameYear = record.education_end_year === parseInt(formData.education_end_year);
      
      return sameInstitution && sameQualification && sameYear;
    });
    
    if (isDuplicate) {
      setDuplicateWarning('A similar education record already exists (same institution, qualification, and year).');
      return true;
    }
    setDuplicateWarning(null);
    return false;
  };

  const handleOpenAdd = () => {
    setEditingRecord(null);
    setShowModal(true);
  };

  const handleOpenEdit = (record) => {
    setEditingRecord(record);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRecord(null);
    setDuplicateWarning(null);
    setInstitutionDisplayName('');
  };

  const handleSelectInstitution = (institutionId) => {
    const inst = institutions.find(i => i.id === institutionId);
    if (inst) {
      setFormData(prev => ({
        ...prev,
        education_institution: inst.id,
        education_institution_other: '',
        isOtherInstitution: false,
      }));
      setInstitutionDisplayName(inst.name);
    }
  };

  const handleCreateCustom = (searchTerm) => {
    setFormData(prev => ({
      ...prev,
      education_institution: null,
      education_institution_other: searchTerm || '',
      isOtherInstitution: true,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.education_institution && !formData.education_institution_other) {
      toast.error('Please select or enter an institution');
      return;
    }
    
    // Check for duplicates
    if (checkDuplicate()) {
      return;
    }
    
    setIsSubmitting(true);
    try {
      const payload = {
        education_staff_id: staffId,
        education_qualification_name: formData.education_qualification_name,
        education_field_of_study: formData.education_field_of_study,
        education_start_year: parseInt(formData.education_start_year),
        education_end_year: formData.education_is_ongoing ? null : parseInt(formData.education_end_year),
        education_is_ongoing: formData.education_is_ongoing,
      };

      if (formData.isOtherInstitution || !formData.education_institution) {
        payload.education_institution = null;
        payload.education_institution_other = formData.education_institution_other;
      } else {
        // Using FK from library
        payload.education_institution = formData.education_institution;
        payload.education_institution_other = '';
      }

      if (editingRecord) {
        await staffAPI.updateEducation(editingRecord.id, payload);
        toast.success('Education record updated successfully');
      } else {
        await staffAPI.createEducation(payload);
        toast.success('Education record added successfully');
      }
      
      handleCloseModal();
      onDataChange();
    } catch (error) {
      console.error('Error saving education record:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.error || 
                          'Failed to save education record';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (recordId) => {
    try {
      await staffAPI.deleteEducation(recordId);
      toast.success('Education record deleted successfully');
      setDeleteConfirm(null);
      onDataChange();
    } catch (error) {
      console.error('Error deleting education record:', error);
      toast.error('Failed to delete education record');
    }
  };

  // Data freshness calculation is now handled by getDataFreshness utility from dateUtils

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Section Header */}
      <div className="flex items-center justify-between gap-2 px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <GraduationCap className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">Education</h3>
            <p className="text-xs sm:text-sm text-gray-500 truncate">{records.length} record{records.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <button
          onClick={handleOpenAdd}
          className="btn btn-admin-primary w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2 btn-sm flex-shrink-0"
          aria-label="Add Education"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Add Education</span>
        </button>
      </div>

      {/* Records Table */}
      {records.length === 0 ? (
        <div className="px-6 py-8 text-center text-gray-500">
          <GraduationCap className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-sm">No education records yet</p>
          <button
            onClick={handleOpenAdd}
            className="mt-3 text-sm text-red-600 hover:text-red-700 font-medium"
          >
            + Add first education record
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Institution</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Qualification</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Year</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Last Modified</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {records.map((record) => {
                const freshness = getDataFreshness(record.education_updated_at);
                return (
                  <tr key={record.id} className="table-row">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {record.institution_display_name || record.education_institution_other || 'N/A'}
                      </div>
                      {record.education_field_of_study && (
                        <div className="text-xs text-gray-500">{record.education_field_of_study}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {record.education_qualification_name || 'N/A'}
                      </div>
                      {record.qualification_level_display && (
                        <div className="text-xs text-gray-500">{record.qualification_level_display}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {record.education_is_ongoing ? (
                        <span className="text-blue-600">Ongoing</span>
                      ) : (
                        record.education_end_year || 'N/A'
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className={`flex items-center gap-1 text-xs ${freshness.isStale ? 'text-amber-600' : 'text-gray-500'}`}>
                        <Clock className="w-3 h-3" />
                        {freshness.label}
                        {freshness.isStale && <AlertTriangle className="w-3 h-3 ml-1" />}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleOpenEdit(record)}
                          className="icon-button-admin"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(record.id)}
                          className="icon-button-danger"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Another Row (Footer) */}
      {records.length > 0 && (
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
          <button
            onClick={handleOpenAdd}
            className="btn btn-admin-outline btn-sm w-full"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Another Education Record
          </button>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <Portal>
          <div className="modal-overlay" onClick={handleCloseModal}>
            <div className="modal-container modal-container-md" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">
                  {editingRecord ? 'Edit Education Record' : 'Add Education Record'}
                </h3>
                <button onClick={handleCloseModal} className="modal-close-admin">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="modal-content space-y-4">
                  {/* Duplicate Warning */}
                  {duplicateWarning && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-red-800">Duplicate Detected</p>
                        <p className="text-sm text-red-700">{duplicateWarning}</p>
                      </div>
                    </div>
                  )}

                  {/* Institution - Searchable Dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Institution Name <span className="text-red-500">*</span>
                    </label>
                    
                    {formData.isOtherInstitution ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          className="input-admin w-full"
                          value={formData.education_institution_other}
                          onChange={(e) => setFormData(prev => ({ ...prev, education_institution_other: e.target.value }))}
                          placeholder="Enter institution name"
                          required
                        />
                        <button
                          type="button"
                          className="text-sm text-blue-600 hover:text-blue-700"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, isOtherInstitution: false, education_institution_other: '' }));
                            setInstitutionDisplayName('');
                          }}
                        >
                          ← Search from library instead
                        </button>
                      </div>
                    ) : (
                      <SearchableCombobox
                        items={institutions}
                        value={formData.education_institution}
                        onChange={handleSelectInstitution}
                        onSearch={fetchInstitutions}
                        loading={isLoadingInstitutions}
                        loadingLabel="Loading institutions..."
                        placeholder="Search institutions (e.g., NUS, NTU, Polytechnic)"
                        searchPlaceholder="Type to search..."
                        emptyLabel="No institutions found"
                        displayFn={(inst) => inst.name}
                        valueFn={(inst) => inst.id}
                        displayValue={institutionDisplayName || undefined}
                        variant="form"
                        theme="admin"
                        className="w-full"
                        creatableLabel={(term) => `+ Add "${term || 'Other'}" as custom institution`}
                        onCreateNew={handleCreateCustom}
                        renderItem={({ item, isSelected, isHighlighted, onSelect }) => (
                          <button
                            key={item.id}
                            type="button"
                            onClick={onSelect}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                              isSelected ? 'bg-red-50 text-red-700 font-medium' : 'text-gray-700'
                            } ${isHighlighted ? 'bg-gray-100' : ''}`}
                          >
                            <div className="font-medium">{item.name}</div>
                            {item.institution_type_name && (
                              <div className="text-xs text-gray-500">{item.institution_type_name}</div>
                            )}
                          </button>
                        )}
                      />
                    )}
                    
                    <p className="mt-1 text-xs text-gray-500">
                      Search from our library of institutions synced from Hipo, or add a custom one.
                    </p>
                  </div>

                  {/* Qualification Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Qualification Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="input-admin w-full"
                      value={formData.education_qualification_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, education_qualification_name: e.target.value }))}
                      placeholder="e.g., Bachelor of Engineering (Civil)"
                      required
                    />
                  </div>

                  {/* Field of Study */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Field of Study
                    </label>
                    <input
                      type="text"
                      className="input-admin w-full"
                      value={formData.education_field_of_study}
                      onChange={(e) => setFormData(prev => ({ ...prev, education_field_of_study: e.target.value }))}
                      placeholder="e.g., Civil Engineering"
                    />
                  </div>

                  {/* Year Range */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Year <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        className="input-admin w-full"
                        value={formData.education_start_year}
                        onChange={(e) => setFormData(prev => ({ ...prev, education_start_year: e.target.value }))}
                        min="1950"
                        max={new Date().getFullYear()}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Year {!formData.education_is_ongoing && <span className="text-red-500">*</span>}
                      </label>
                      <input
                        type="number"
                        className="input-admin w-full"
                        value={formData.education_end_year}
                        onChange={(e) => setFormData(prev => ({ ...prev, education_end_year: e.target.value }))}
                        min="1950"
                        max={new Date().getFullYear() + 5}
                        disabled={formData.education_is_ongoing}
                        required={!formData.education_is_ongoing}
                      />
                    </div>
                  </div>

                  {/* Ongoing Checkbox */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="education_is_ongoing"
                      className="w-4 h-4 text-red-600 border-gray-300 rounded focus-visible:ring-red-500"
                      checked={formData.education_is_ongoing}
                      onChange={(e) => setFormData(prev => ({ ...prev, education_is_ongoing: e.target.checked }))}
                    />
                    <label htmlFor="education_is_ongoing" className="text-sm text-gray-700">
                      Currently studying (ongoing)
                    </label>
                  </div>

                  {/* Edit Mode: Last Modified Info */}
                  {editingRecord && (
                    <div className="text-xs text-gray-500 pt-2 border-t border-gray-100">
                      Last modified: {formatDate(editingRecord.education_updated_at)}
                    </div>
                  )}
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="btn btn-admin-outline"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-admin-primary"
                    disabled={isSubmitting || duplicateWarning}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="loading-spinner-sm mr-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        {editingRecord ? 'Update' : 'Add'} Record
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </Portal>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <Portal>
          <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
            <div className="modal-container modal-container-sm" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">Delete Education Record</h3>
                <button onClick={() => setDeleteConfirm(null)} className="modal-close-admin">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="modal-content">
                <p className="text-sm text-gray-700">
                  Are you sure you want to delete this education record? This action cannot be undone.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="btn btn-admin-outline"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="btn btn-danger"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
};

export default EducationSection;
